import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import liff from "@line/liff";

import { LoadingOverlay } from "@/apps/@components";
import { api, ApiError } from "@utils/index";
import { LogSystemApi } from "@utils/api/analytics/log-system.api";
import { logWallpaperEvent } from "./wallpaperLog";
import { markLiffFlow } from "./wallpaperParams";

/**
 * 台灣 LIFF 版入口（仿 campaign/splash-2026 的自帶判斷 pattern）。
 *
 * 連結：{base_liff_url}/wallpaper?from=wallpaper
 *  1. liff.init → 未登入則 liff.login
 *  2. 換主 api_token（此頁在 LoginGuard 外，必須自己換，否則 /profile 會 401）
 *  3. GET /profile 判斷是否已綁定會員（既有判斷）
 *     - 已綁定 → 進入桌布首頁 /wallpaper/home
 *     - 未綁定 → 導去 /binding?from=wallpaper，綁定成功後由 BindingPage 帶回 /wallpaper/home
 */
const HOME_PATH = "/wallpaper/home";
const BINDING_FROM = "wallpaper";
const isLineLogin = process.env.REACT_APP_LIFF_LOGIN;

export default function WallpaperLiffPage() {
	const navigate = useNavigate();
	// 避免 StrictMode / 重渲染重複觸發整段流程
	const startedRef = useRef(false);

	// 換主 api_token
	const ensureApiToken = async () => {
		try {
			const accessToken = liff.getAccessToken() ?? "";
			const profile = await liff.getProfile();
			const res = await api.post("/login", {
				line_uid: profile.userId,
				access_token: accessToken,
				friendship: true,
				friendship_status_changed: false,
			});
			const apiToken = (res as { access_token?: string })?.access_token;
			if (apiToken) {
				localStorage.setItem("api_token", apiToken);
			}
		} catch (err) {
			console.error("[wallpaper-liff] ensureApiToken failed", err);
		}
	};

	const ensureLogToken = async () => {
		try {
			if (localStorage.getItem("log_token")) return;
			const accessToken = liff.getAccessToken() ?? "";
			const profile = await liff.getProfile();
			const res = await LogSystemApi.loginEvent({
				line_uid: profile.userId,
				line_name: profile.displayName,
				liff_id: process.env.REACT_APP_LIFF_ID ?? "",
				access_token: accessToken,
			});
			if (res?.jwt) {
				localStorage.setItem("log_token", res.jwt);
			}
		} catch (err) {
			console.error("[wallpaper-liff] ensureLogToken failed", err);
		}
	};

	// 判斷綁定狀態並導向
	const checkBindingAndRoute = async () => {
		try {
			await api.get("/profile");
			// 已綁定 → 進入活動
			navigate(HOME_PATH, { replace: true });
		} catch (err) {
			const errorCode = (
				err as { response?: { data?: { error?: { code?: number } } } }
			)?.response?.data?.error?.code;
			// 未綁定（或未知錯誤）→ 導去綁定頁，綁完由 BindingPage 依 from 帶回
			if (errorCode !== ApiError.USER_NOT_BINDED) {
				console.error("[wallpaper-liff] checkBinding failed", err);
			}
			// 未綁定 → 導去綁定頁
			logWallpaperEvent({
				category: "cta",
				path: "/wallpaper/member_bind",
				action: "start",
				event: "cta_start",
			});
			navigate(`/binding?from=${BINDING_FROM}`, { replace: true });
		}
	};

	useEffect(() => {
		if (startedRef.current) return;
		startedRef.current = true;

		(async () => {
			// 本機 / 非 LINE 環境（REACT_APP_LIFF_LOGIN !== "true"）直接進首頁，方便開發
			if (isLineLogin !== "true") {
				navigate(HOME_PATH, { replace: true });
				return;
			}
			try {
				await liff.init({ liffId: process.env.REACT_APP_LIFF_ID as string });
				if (!liff.isLoggedIn()) {
					liff.login({ redirectUri: window.location.href });
					return;
				}
				// 走到這裡確定是 LIFF 流程：先標記，後續各頁的驗證 / 上報都靠它分流
				markLiffFlow();
				await ensureApiToken();
				await ensureLogToken();
				await checkBindingAndRoute();
			} catch (err) {
				console.error("[wallpaper-liff] LIFF init failed", err);
				// init 失敗就退回 LIFF 入口讓整個流程重跑
				const liffUrl = process.env.REACT_APP_LIFF_URL;
				if (liffUrl) {
					window.location.href = liffUrl;
				}
			}
		})();
		// 僅初始化跑一次
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return <LoadingOverlay visible />;
}
